import React, { useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Grid
} from '@material-ui/core';

const getValidationSchema = (isEdit) => {
    if (isEdit) {
        return Yup.object().shape({
            pds_d: Yup.number()
                .required('Poids donné obligatoire.')
                .typeError('Poids donné doit être un nombre.')
                .min(0, 'Le poids doit être positif.'),
            pds_r: Yup.number()
                .typeError('Poids retourné doit être un nombre.')
                .min(0, 'Le poids doit être positif.'),
            prix: Yup.number()
                .typeError('Prix doit être un nombre.')
                .min(0, 'Le prix doit être positif.'),
        });
    }
    
    return Yup.object().shape({
        pds_d: Yup.number()
            .required('Poids donné obligatoire.')
            .typeError('Poids donné doit être un nombre.')
            .min(0, 'Le poids doit être positif.'),
        pds_r: Yup.number()
            .required('Poids retourné obligatoire.')
            .typeError('Poids retourné doit être un nombre.')
            .min(0, 'Le poids doit être positif.'),
        prix: Yup.number()
            .required('Prix obligatoire.')
            .typeError('Prix doit être un nombre.')
            .min(0, 'Le prix doit être positif.'),
    });
};

const SertisseurEditModal = ({ 
    open, 
    rowData, 
    onClose, 
    onSave, 
    loading = false 
}) => {
    const poidsDRef = useRef(null);
    const isEdit = !!rowData?.id_ligne;

    const formik = useFormik({
        initialValues: {
            pds_d: rowData?.pds_d || '',
            pds_r: rowData?.pds_r || '',
            prix: rowData?.prix || '',
        },
        validationSchema: getValidationSchema(isEdit),
        onSubmit: async (values) => {
            try {
                await onSave({
                    id_ligne: rowData.id_ligne,
                    id_sert: rowData.id_sert || rowData.id || null,
                    of: rowData.of_serto || rowData.of || null,
                    of_serto: rowData.of_serto,
                    date_retour: rowData.date_retour || rowData.date_r || null,
                    pds_d: parseFloat(values.pds_d),
                    pds_r: parseFloat(values.pds_r),
                    prix: parseFloat(values.prix),
                    rendu_serti: rowData.rendu_serti,
                });
                onClose();
            } catch (error) {
                console.error('Erreur lors de la sauvegarde :', error);
            }
        },
    });

    const { values, errors, touched, handleChange, handleSubmit, handleBlur } = formik;

    useEffect(() => {
        if (open && poidsDRef.current) {
            setTimeout(() => poidsDRef.current?.focus(), 100);
        }
    }, [open]);

    useEffect(() => {
        if (rowData) {
            formik.setValues({
                pds_d: rowData.pds_d || '',
                pds_r: rowData.pds_r || '',
                prix: rowData.prix || '',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Modifier le mouvement</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Grid container spacing={2} style={{ marginTop: 0 }}>
                        {/* Poids donné */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Poids donné (g)"
                                type="number"
                                inputProps={{ step: "0.01", min: "0" }}
                                name="pds_d"
                                value={values.pds_d}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.pds_d && !!errors.pds_d}
                                helperText={touched.pds_d ? errors.pds_d : 'Poids donné au sertisseur'}
                                variant="outlined"
                                disabled={loading}
                                inputRef={poidsDRef}
                                size="small"
                            />
                        </Grid>

                        {/* Poids retourné */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Poids retourné (g)"
                                type="number"
                                inputProps={{ step: "0.01", min: "0" }}
                                name="pds_r"
                                value={values.pds_r}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.pds_r && !!errors.pds_r}
                                helperText={touched.pds_r ? errors.pds_r : 'Poids retourné par le sertisseur'}
                                variant="outlined"
                                disabled={loading}
                                size="small"
                            />
                        </Grid>

                        {/* Prix */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Prix"
                                type="number"
                                inputProps={{ step: "0.01", min: "0" }}
                                name="prix"
                                value={values.prix}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.prix && !!errors.prix}
                                helperText={touched.prix ? errors.prix : 'Prix de la sertissure'}
                                variant="outlined"
                                disabled={loading}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions style={{ padding: '16px' }}>
                    <Button onClick={onClose} disabled={loading}>
                        Annuler
                    </Button>
                    <Button 
                        type="submit" 
                        color="primary" 
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default SertisseurEditModal;
